import {createSlice, PayloadAction} from '@reduxjs/toolkit'
import {ChatbotResponse} from '../../../models'
import {
  fetchChatbotResponses,
  fetchActiveChatbotResponses,
  createChatbotResponse,
  updateChatbotResponse,
  deleteChatbotResponse,
  toggleChatbotResponseStatus,
  searchChatbotResponses,
} from './service'
import {ChatbotState} from './types'

const initialState: ChatbotState = {
  listLoading: false,
  actionLoading: false,
  error: null,
  responseForEdit: null,
  responses: [],
  total: 0,
  totalPages: 0,
}

const chatbotSlice = createSlice({
  name: 'chatbot',
  initialState,
  reducers: {
    setResponseForEdit: (state, action: PayloadAction<ChatbotResponse | null>) => {
      state.responseForEdit = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
    clearResponses: (state) => {
      state.responses = []
      state.total = 0
      state.totalPages = 0
    },
  },
  extraReducers: (builder) => {
    // Fetch chatbot responses
    builder
      .addCase(fetchChatbotResponses.pending, (state) => {
        state.listLoading = true
        state.error = null
      })
      .addCase(fetchChatbotResponses.fulfilled, (state, action) => {
        state.listLoading = false
        console.log('Redux slice received payload:', action.payload)
        console.log('Responses from payload:', action.payload.data?.responses)
        console.log('Total from payload:', action.payload.data?.total)
        console.log('TotalPages from payload:', action.payload.data?.totalPages)
        
        // Handle the API response structure
        state.responses = action.payload.data?.responses || []
        state.total = action.payload.data?.total || 0
        state.totalPages = action.payload.data?.totalPages || 0
        state.error = null
      })
      .addCase(fetchChatbotResponses.rejected, (state, action) => {
        state.listLoading = false
        state.error = action.payload as string
      })

    // Fetch active chatbot responses
    builder
      .addCase(fetchActiveChatbotResponses.pending, (state) => {
        state.listLoading = true
        state.error = null
      })
      .addCase(fetchActiveChatbotResponses.fulfilled, (state, action) => {
        state.listLoading = false
        state.responses = action.payload.data.responses
        state.error = null
      })
      .addCase(fetchActiveChatbotResponses.rejected, (state, action) => {
        state.listLoading = false
        state.error = action.payload as string
      })

    // Create chatbot response
    builder
      .addCase(createChatbotResponse.pending, (state) => {
        state.actionLoading = true
        state.error = null
      })
      .addCase(createChatbotResponse.fulfilled, (state, action) => {
        state.actionLoading = false
        console.log('Create response payload:', action.payload)
        console.log('New response:', action.payload.data)
        // The API returns the response directly in data, not data.response
        if (action.payload.data) {
          state.responses.unshift(action.payload.data)
          state.total += 1
        }
        state.error = null
      })
      .addCase(createChatbotResponse.rejected, (state, action) => {
        state.actionLoading = false
        state.error = action.payload as string
      })

    // Update chatbot response
    builder
      .addCase(updateChatbotResponse.pending, (state) => {
        state.actionLoading = true
        state.error = null
      })
      .addCase(updateChatbotResponse.fulfilled, (state, action) => {
        state.actionLoading = false
        console.log('Update response payload:', action.payload)
        console.log('Update response data:', action.payload.data)
        
        // Handle the API response structure
        const updatedResponse = action.payload.data
        if (updatedResponse && updatedResponse._id) {
          const index = state.responses.findIndex(
            (response) => response._id === updatedResponse._id
          )
          if (index !== -1) {
            state.responses[index] = updatedResponse
          }
        }
        state.responseForEdit = null
        state.error = null
      })
      .addCase(updateChatbotResponse.rejected, (state, action) => {
        state.actionLoading = false
        state.error = action.payload as string
      })

    // Delete chatbot response
    builder
      .addCase(deleteChatbotResponse.pending, (state) => {
        state.actionLoading = true
        state.error = null
      })
      .addCase(deleteChatbotResponse.fulfilled, (state, action) => {
        state.actionLoading = false
        state.responses = state.responses.filter(
          (response) => response._id !== action.meta.arg.id
        )
        state.total -= 1
        state.error = null
      })
      .addCase(deleteChatbotResponse.rejected, (state, action) => {
        state.actionLoading = false
        state.error = action.payload as string
      })

    // Toggle chatbot response status
    builder
      .addCase(toggleChatbotResponseStatus.pending, (state) => {
        state.actionLoading = true
        state.error = null
      })
      .addCase(toggleChatbotResponseStatus.fulfilled, (state, action) => {
        state.actionLoading = false
        console.log('Toggle status payload:', action.payload)
        console.log('Toggle status data:', action.payload.data)
        
        // Handle the API response structure
        const updatedResponse = action.payload.data
        if (updatedResponse && updatedResponse._id) {
          const index = state.responses.findIndex(
            (response) => response._id === updatedResponse._id
          )
          if (index !== -1) {
            state.responses[index] = updatedResponse
          }
        }
        state.error = null
      })
      .addCase(toggleChatbotResponseStatus.rejected, (state, action) => {
        state.actionLoading = false
        state.error = action.payload as string
      })

    // Search chatbot responses
    builder
      .addCase(searchChatbotResponses.pending, (state) => {
        state.listLoading = true
        state.error = null
      })
      .addCase(searchChatbotResponses.fulfilled, (state, action) => {
        state.listLoading = false
        // For search, we might want to store results differently
        // This depends on how you want to handle search results
        state.error = null
      })
      .addCase(searchChatbotResponses.rejected, (state, action) => {
        state.listLoading = false
        state.error = action.payload as string
      })
  },
})

export const {setResponseForEdit, clearError, clearResponses} = chatbotSlice.actions
export default chatbotSlice.reducer
